import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Warning, PencilSimple } from '@phosphor-icons/react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (x) => {
  try { return `€ ${parseFloat(x).toFixed(2).replace('.', ',')}`; } catch { return `€ ${x}`; }
};

/**
 * BevestigBestellingPage — publieke pagina (geen login) die een leverancier
 * gebruikt om een aankoopbestelling te bevestigen of aan te passen.
 */
export default function BevestigBestellingPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [regels, setRegels] = useState([]);
  const [bericht, setBericht] = useState('');
  const [modus, setModus] = useState('view'); // 'view' | 'edit'
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/public/aankoopbestelling/${token}`)
      .then(r => {
        setData(r.data);
        setRegels((r.data.bestelling.lijnen || []).map(l => ({ ...l })));
      })
      .catch(e => setError(e?.response?.data?.detail || 'Bestelling niet gevonden'));
  }, [token]);

  const updRegel = (i, k, v) => setRegels(p => p.map((r, j) => j === i ? { ...r, [k]: v } : r));
  const totaalExcl = regels.reduce((s, r) => s + (parseFloat(r.aantal) || 0) * (parseFloat(r.prijs) || 0), 0);

  const submit = async (actie) => {
    setSubmitting(true);
    try {
      const body = { actie, bericht };
      if (actie === 'gewijzigd') {
        body.regels = regels.map(r => ({ art_id: r.art_id, aantal: parseFloat(r.aantal) || 0, prijs: parseFloat(r.prijs) || 0 }));
      }
      const r = await axios.post(`${API_URL}/public/aankoopbestelling/${token}/bevestig`, body);
      setResult({ actie, status: r.data.status });
    } catch (e) {
      alert('Versturen mislukt: ' + (e?.response?.data?.detail || e.message));
    } finally { setSubmitting(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="bg-white border border-red-200 p-8 max-w-md text-center">
          <Warning size={48} className="mx-auto text-red-500 mb-4" weight="duotone" />
          <h2 className="text-lg font-bold text-gray-900 mb-2">Bestelling niet gevonden</h2>
          <p className="text-sm text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="min-h-screen flex items-center justify-center text-gray-500">Laden…</div>;
  }

  if (result) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="bg-white border border-green-200 p-8 max-w-md text-center" data-testid="bevestig-success">
          <CheckCircle size={56} className="mx-auto text-green-600 mb-4" weight="duotone" />
          <h2 className="text-lg font-bold text-gray-900 mb-2">Bedankt!</h2>
          <p className="text-sm text-gray-600">
            {result.actie === 'bevestigd'
              ? 'De bestelling is bevestigd. De besteller wordt op de hoogte gebracht.'
              : 'De wijzigingen zijn doorgestuurd naar de besteller.'}
          </p>
          <div className="mt-4 text-[11px] text-gray-400">Status: <strong className="text-green-700">{result.status}</strong></div>
        </div>
      </div>
    );
  }

  const b = data.bestelling;
  const afz = data.afzender;
  const alReedsBevestigd = ['bevestigd', 'gewijzigd'].includes(b.status);

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-3xl mx-auto bg-white shadow-lg">
        {/* Header */}
        <div className="px-8 py-6 border-b-4 border-[#002FA7] flex items-start justify-between">
          <div>
            <div className="text-2xl font-bold text-[#002FA7]">Aankoopbestelling</div>
            <div className="font-mono text-gray-600 mt-1">{b.id}</div>
          </div>
          <div className="text-right text-xs text-gray-500">
            <div>Datum: <strong>{b.datum}</strong></div>
            <div>Status: <strong className="text-[#002FA7]">{b.status}</strong></div>
          </div>
        </div>

        {/* Afzender */}
        <div className="px-8 py-4 bg-gray-50 border-b border-gray-200 grid grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-[10px] uppercase text-gray-500 font-semibold tracking-wider mb-1">Besteller</div>
            <div className="font-semibold">{afz.bedrijfsnaam || '—'}</div>
            {afz.email && <div className="text-xs text-gray-600">{afz.email}</div>}
            {afz.btw && <div className="text-xs text-gray-500">BTW: {afz.btw}</div>}
          </div>
          <div>
            <div className="text-[10px] uppercase text-gray-500 font-semibold tracking-wider mb-1">Leverancier</div>
            <div className="font-semibold">{b.leverancier}</div>
          </div>
        </div>

        {/* Regels */}
        <div className="px-8 py-6">
          {alReedsBevestigd && (
            <div className="mb-4 bg-amber-50 border-l-4 border-amber-400 p-3 text-sm">
              ⚠ Deze bestelling is al <strong>{b.status}</strong>. Een nieuwe bevestiging zal de status opnieuw updaten.
            </div>
          )}

          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold text-gray-900">Regels</div>
            {modus === 'view' ? (
              <button
                onClick={() => setModus('edit')}
                className="text-[11px] text-[#002FA7] hover:underline inline-flex items-center gap-1"
                data-testid="bevestig-edit-btn"
              >
                <PencilSimple size={12} /> Aantallen/prijzen aanpassen
              </button>
            ) : (
              <button onClick={() => { setModus('view'); setRegels((b.lijnen || []).map(l => ({ ...l }))); }} className="text-[11px] text-gray-500 hover:underline">Annuleer wijziging</button>
            )}
          </div>

          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-gray-200">
                <th className="text-left py-2 text-[10px] uppercase text-gray-500 font-semibold">Art. code</th>
                <th className="text-left py-2 text-[10px] uppercase text-gray-500 font-semibold">Omschrijving</th>
                <th className="text-right py-2 text-[10px] uppercase text-gray-500 font-semibold" style={{ width: 90 }}>Aantal</th>
                <th className="text-right py-2 text-[10px] uppercase text-gray-500 font-semibold" style={{ width: 110 }}>Prijs</th>
                <th className="text-right py-2 text-[10px] uppercase text-gray-500 font-semibold" style={{ width: 110 }}>Totaal</th>
              </tr>
            </thead>
            <tbody>
              {regels.map((r, i) => (
                <tr key={i} className="border-b border-gray-100">
                  <td className="py-2 font-mono text-xs text-[#002FA7] font-semibold">{r.art_code || '-'}</td>
                  <td className="py-2">
                    {r.omschrijving}
                    {r.ean && <div className="text-[10px] text-gray-400 font-mono">EAN: {r.ean}</div>}
                  </td>
                  <td className="py-2 text-right">
                    {modus === 'edit' ? (
                      <input type="number" min="0" value={r.aantal} onChange={e => updRegel(i, 'aantal', e.target.value)} className="field-input text-right" data-testid={`bevestig-aantal-${i}`} />
                    ) : r.aantal}
                  </td>
                  <td className="py-2 text-right">
                    {modus === 'edit' ? (
                      <input type="number" min="0" step="0.01" value={r.prijs} onChange={e => updRegel(i, 'prijs', e.target.value)} className="field-input text-right" data-testid={`bevestig-prijs-${i}`} />
                    ) : fmt(r.prijs)}
                  </td>
                  <td className="py-2 text-right font-semibold">{fmt((parseFloat(r.aantal) || 0) * (parseFloat(r.prijs) || 0))}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-4 text-right space-y-1 border-t-2 border-gray-200 pt-3">
            <div className="text-xs text-gray-500">Excl. BTW: {fmt(totaalExcl)}</div>
            <div className="text-xs text-gray-500">BTW (21%): {fmt(totaalExcl * 0.21)}</div>
            <div className="text-lg font-bold">Totaal: {fmt(totaalExcl * 1.21)}</div>
          </div>

          <div className="mt-6">
            <label className="text-xs text-gray-600 font-semibold">Bericht aan besteller (optioneel)</label>
            <textarea
              className="field-input"
              rows="3"
              value={bericht}
              onChange={e => setBericht(e.target.value)}
              placeholder="Bv. 'Levering 14 dagen' of 'Voorraad beperkt'…"
              data-testid="bevestig-bericht"
            />
          </div>
        </div>

        {/* Footer-acties */}
        <div className="px-8 py-5 bg-gray-50 border-t border-gray-200 flex items-center justify-end gap-2">
          {modus === 'edit' ? (
            <button
              disabled={submitting}
              onClick={() => submit('gewijzigd')}
              className="btn btn-primary inline-flex items-center gap-1.5"
              data-testid="bevestig-submit-wijziging"
            >
              <PencilSimple size={15} /> Wijziging indienen
            </button>
          ) : (
            <button
              disabled={submitting}
              onClick={() => submit('bevestigd')}
              className="btn btn-primary inline-flex items-center gap-1.5"
              style={{ background: '#16a34a' }}
              data-testid="bevestig-submit"
            >
              <CheckCircle size={15} /> Bestelling bevestigen
            </button>
          )}
        </div>

        <div className="px-8 py-3 text-[10px] text-gray-400 text-center">
          Deze bevestigingspagina is beveiligd met een unieke link. Bij vragen: contacteer <strong>{afz.email || afz.bedrijfsnaam}</strong>.
        </div>
      </div>
    </div>
  );
}
